import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
    getVendorDocumentsRequest,
    uploadVendorDocumentRequest,
    deleteVendorDocumentRequest,
    updateVendorDocumentStatusRequest,
} from "../../../redux/vendor/documents/action";
import {
    VendorSearchSvg,
    VendorUploadSvg,
    VendorBackSvg,
    VendorFunnelSvg,
} from "../../../components/CommonSvg";
import Select from "react-select";
import CustomisedModal from "../../../components/Modal/CustomisedModal";
import { commonConfirmBox } from "../../../helper/messagehelpers";
import { ApiResponseMessage } from "../../../helper/tosterHelper";
import { DANGER } from "../../../constant/commonConstants";
import UploadDocument from "./UploadDocument";
import ShareDocumentModal from "./ShareDocumentModal";
import DocumentDetailsModal from "./DocumentDetailsModal";
import DocumentFolders from "./DocumentFolders";
import DocumentRow from "./DocumentRow";
import { useDocumentShare } from "./documentShareApi";
import { PER_PAGE, downloadDocument } from "./documentUtils";

/* the filter collapses to its icon on a phone, where the words cost more room
   than the search they sit beside - the menu still says which one is on, and a
   dot on the control says that one is */
const FilterIndicator = () => (
    <span className="vp-dsel-ico">
        <VendorFunnelSvg />
    </span>
);

/* the menu is portalled to <body>, so it is out of reach of the stylesheet
   block the control lives in - its sizing has to be set here. On a phone the
   control is a 42px circle; the menu keeps its own width and hangs from the
   right edge so it opens back over the toolbar rather than off the screen. */
const FILTER_STYLES = {
    menuPortal: (base) => ({ ...base, zIndex: 1100 }),
    menu: (base) => ({
        ...base,
        width: "max-content",
        minWidth: "100%",
        maxWidth: 220,
        left: "auto",
        right: 0,
        fontSize: 13.5,
        borderRadius: 10,
        overflow: "hidden",
    }),
    option: (base) => ({ ...base, whiteSpace: "nowrap", cursor: "pointer" }),
};

const SOURCE_FILTERS = [
    { value: "", label: "All Documents" },
    { value: "Vendor", label: "Shared by You" },
    { value: "Admin", label: "Shared by Admin" },
];

const VendorDocuments = ({
    list,
    categories,
    counts,
    totalRecords,
    totalPages,
    loading,
    canUpload,
    canDownload,
    uploading,
    uploadResult,
    statusUpdating,
    getVendorDocuments,
    uploadVendorDocument,
    deleteVendorDocument,
    updateVendorDocumentStatus,
}) => {
    /* the dashboard links at a file's folder - the folder opens straight away,
       the file itself is left for the user to open */
    const [searchParams] = useSearchParams();
    const linkedFolder = searchParams.get("folder");

    const [category, setCategory] = useState(linkedFolder || "");
    const [view, setView] = useState(linkedFolder === null ? "grid" : "list");
    const [uploadBy, setUploadBy] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [shareFor, setShareFor] = useState(null);
    const [viewFor, setViewFor] = useState(null);
    const [menuFor, setMenuFor] = useState(null);

    const listQuery = useMemo(
        () => ({
            category: category,
            uploadBy: uploadBy,
            searchText: search,
            pagination: { page: page, limit: PER_PAGE },
        }),
        [category, uploadBy, search, page]
    );

    const { targets, loadTargets, share, sharing } =
        useDocumentShare(listQuery);

    useEffect(() => {
        getVendorDocuments(listQuery);
    }, [listQuery]);

    useEffect(() => {
        setPage(1);
    }, [category, uploadBy, search]);

    useEffect(() => {
        if (uploadResult?.status) {
            setUploadOpen(false);
        }
    }, [uploadResult?.at]);

    useEffect(() => {
        if (!menuFor) return undefined;
        const close = () => setMenuFor(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [menuFor]);

    const handleDownload = async (document) => {
        const result = await downloadDocument(document);
        if (!result.status) {
            ApiResponseMessage(result.message, DANGER);
        }
    };

    const handleDelete = async (document) => {
        const confirmed = await commonConfirmBox(
            `Do you want to delete "${document.documentName}"?`
        );
        if (confirmed) {
            deleteVendorDocument({
                documentId: document.documentId,
                listQuery: listQuery,
            });
        }
    };

    const handleToggleStatus = async (document) => {
        const confirmed = await commonConfirmBox(
            "",
            undefined,
            undefined,
            document.isActive
                ? `Do you want to deactivate "${document.documentName}"?`
                : `Do you want to activate "${document.documentName}"?`
        );
        if (confirmed) {
            updateVendorDocumentStatus({
                documentId: document.documentId,
                isActive: !document.isActive,
                listQuery: listQuery,
            });
        }
    };

    const viewRow =
        viewFor && (list.find((row) => row.documentId === viewFor.documentId) || viewFor);

    const inFolder = view === "list";
    const showCategoryTag = category === "";
    const openFolderLabel = category
        ? categories.find((folder) => folder.id === category)?.name || "Documents"
        : "All Documents";

    /* a folder is opened fresh - the search and the source filter belong to the
       folder that was being looked at, and carrying them in made a folder look
       empty for reasons that were two screens back */
    const openFolder = (folderId) => {
        setCategory(folderId);
        setView("list");
        setSearch("");
        setUploadBy("");
    };

    const closeFolder = () => {
        setView("grid");
        setCategory("");
        setSearch("");
        setUploadBy("");
    };

    const totalFiles = Object.values(counts).reduce(
        (total, count) => total + Number(count || 0),
        0
    );

    return (
        <div className="vp-page">
            <div className="vp-mhead-top vp-dhead">
                <div className="vp-dhead-title">
                    <div className="vp-dtitle">Document Management</div>
                </div>
                <div className="vp-dtoolbar">
                    {canUpload && (
                        <button
                            type="button"
                            className="vp-upload-btn"
                            onClick={() => setUploadOpen(true)}
                        >
                            <VendorUploadSvg />
                            Upload
                        </button>
                    )}
                    {inFolder && (
                        <>
                            <div className="vp-dsearch">
                                <VendorSearchSvg />
                                <input
                                    type="text"
                                    value={search}
                                    placeholder="Search documents…"
                                    onChange={(event) => setSearch(event.target.value)}
                                />
                            </div>
                            <Select
                                className={`vp-doc-source${uploadBy ? " on" : ""}`}
                                classNamePrefix="vp-dsel"
                                options={SOURCE_FILTERS}
                                value={SOURCE_FILTERS.find(
                                    (option) => option.value === uploadBy
                                )}
                                onChange={(option) => setUploadBy(option ? option.value : "")}
                                isSearchable={false}
                                isClearable={false}
                                components={{ DropdownIndicator: FilterIndicator }}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={FILTER_STYLES}
                            />
                        </>
                    )}
                </div>
            </div>

            {!inFolder && (
                <DocumentFolders
                    categories={categories}
                    counts={counts}
                    totalFiles={totalFiles}
                    loading={loading}
                    onOpen={openFolder}
                />
            )}

            {inFolder && (
                <>
                    <div className="vp-dsection">
                        <button
                            type="button"
                            className="vp-doc-back"
                            onClick={closeFolder}
                        >
                            <VendorBackSvg />
                        </button>
                        <h3 className="vp-dsection-title">{openFolderLabel}</h3>
                        <span className="vp-dsection-count">
                            {totalRecords}{" "}
                            {totalRecords === 1 ? "document" : "documents"}
                        </span>
                    </div>

                    <div className="vp-dlist">
                        {list.map((document) => (
                            <DocumentRow
                                key={document.documentId}
                                document={document}
                                canUpload={canUpload}
                                canDownload={canDownload}
                                statusUpdating={statusUpdating}
                                showCategoryTag={showCategoryTag}
                                menuOpen={menuFor === document.documentId}
                                onToggleMenu={(row) =>
                                    setMenuFor((current) =>
                                        current === row.documentId ? null : row.documentId
                                    )
                                }
                                onDetails={(row) => {
                                    setViewFor(row);
                                    setMenuFor(null);
                                }}
                                onDownload={(row) => {
                                    handleDownload(row);
                                    setMenuFor(null);
                                }}
                                onShare={(row) => {
                                    setShareFor(row);
                                    setMenuFor(null);
                                }}
                                onDelete={(row) => {
                                    handleDelete(row);
                                    setMenuFor(null);
                                }}
                                onToggleStatus={handleToggleStatus}
                            />
                        ))}

                        {list.length === 0 && (
                            <div className="vp-dempty">
                                {loading
                                    ? "Loading documents…"
                                    : search
                                    ? "No documents match your search."
                                    : "No documents in this folder yet."}
                            </div>
                        )}
                    </div>
                </>
            )}

            {inFolder && totalPages > 1 && (
                <div className="vp-doc-pager">
                    <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((current) => current - 1)}
                    >
                        Previous
                    </button>
                    <span>
                        Page {page} of {totalPages}
                    </span>
                    <button
                        type="button"
                        disabled={page >= totalPages}
                        onClick={() => setPage((current) => current + 1)}
                    >
                        Next
                    </button>
                </div>
            )}

            {uploadOpen && (
                <CustomisedModal
                    modalView
                    customClass="vp-modal"
                    bodyClass="vp-tk-mbody"
                    modalName={"Upload Document"}
                    onModalDismiss={() => setUploadOpen(false)}
                >
                    <UploadDocument
                        categories={categories}
                        uploading={uploading}
                        defaultCategory={category}
                        onCancel={() => setUploadOpen(false)}
                        onSubmit={(formData) =>
                            uploadVendorDocument({
                                formData: formData,
                                listQuery: listQuery,
                            })
                        }
                    />
                </CustomisedModal>
            )}

            {shareFor && (
                <CustomisedModal
                    modalView
                    customClass="vp-modal"
                    bodyClass="vp-tk-mbody vp-share-mbody"
                    modalName={"Share Document"}
                    onModalDismiss={() => setShareFor(null)}
                >
                    <ShareDocumentModal
                        key={shareFor.documentId}
                        document={shareFor}
                        {...targets}
                        sharing={sharing}
                        onLoadTargets={loadTargets}
                        onCancel={() => setShareFor(null)}
                        onShared={() => setShareFor(null)}
                        onSubmit={(data) => share(data)}
                    />
                </CustomisedModal>
            )}

            {viewFor && (
                <CustomisedModal
                    modalView
                    customClass="vp-modal"
                    bodyClass="vp-tk-mbody"
                    modalName={"Document Details"}
                    onModalDismiss={() => setViewFor(null)}
                >
                    <DocumentDetailsModal
                        document={viewRow}
                        onDownload={canDownload ? handleDownload : undefined}
                    />
                </CustomisedModal>
            )}
        </div>
    );
};

const mapStateToProps = ({ VendorDocuments: documents }) => {
    return {
        list: documents?.list || [],
        categories: documents?.categories || [],
        counts: documents?.counts || {},
        totalRecords: documents?.totalRecords || 0,
        totalPages: documents?.totalPages || 0,
        loading: documents?.loading || false,
        canUpload: documents?.canUpload || false,
        canDownload: documents?.canDownload || false,
        uploading: documents?.uploading || false,
        uploadResult: documents?.uploadResult || null,
        statusUpdating: documents?.statusUpdating || false,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getVendorDocuments: (data) => dispatch(getVendorDocumentsRequest(data)),
        uploadVendorDocument: (data) => dispatch(uploadVendorDocumentRequest(data)),
        deleteVendorDocument: (data) => dispatch(deleteVendorDocumentRequest(data)),
        updateVendorDocumentStatus: (data) =>
            dispatch(updateVendorDocumentStatusRequest(data)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorDocuments);
